import { createAsyncThunk } from '@reduxjs/toolkit';
import qs from 'qs';
import { trackSyiForm } from 'tradera/apps/syi/services/syi-ga4-service';
import { setLongDescription } from 'tradera/apps/syi/state/reducers/long-description';
import {
    setAiProcessingTime,
    setHasUsedPredictedDescription,
    setNumProcessedImages,
    setPredictedLongDescription,
    setPredictedShortDescription,
    setShouldSuggestLongDescription,
    setShouldSuggestShortDescription
} from 'tradera/apps/syi/state/reducers/predictions-reducer';
import { setShortDescription } from 'tradera/apps/syi/state/reducers/short-description';
import { selectDraftId } from 'tradera/apps/syi/state/selectors/draft-selectors';
import { selectImageIds } from 'tradera/apps/syi/state/selectors/image-selectors';
import { selectClassificationResponse } from 'tradera/apps/syi/state/selectors/predictions-selectors';
import {
    selectLongDescription,
    selectShortDescription
} from 'tradera/apps/syi/state/selectors/syi';
import type { RootState } from 'tradera/state/configure-store';
import { selectIsFeatureEnabled } from 'tradera/state/environment/selectors';
import { selectPreferredLanguageCode } from 'tradera/state/language/selectors';
import type {
    ClassifyFromDraftImageApiResponse,
    ClassifyFromImageRequest,
    ConditionListingTypeFromCategoryRequest,
    GetConditionAndListingTypeFromCategoryResponse,
    PredictionsFromDraftImageApiResponse
} from 'tradera/state/services/types/webapi-selling-generated';
import { webApiClient } from 'tradera/utils/http';
import draftActions from 'tradera/apps/syi/state/actions/draft-actions';
import priceActions from 'tradera/apps/syi/state/actions/price-actions';
import ItemTypes from 'tradera/constants/item-types';

export const fetchClassificationFromFirstImage = createAsyncThunk(
    'syi/images/fetchClassificationFromFirstImage',
    async ({ imageId, draftId }: ClassifyFromImageRequest) => {
        const response =
            await webApiClient.get<ClassifyFromDraftImageApiResponse>(
                `/selling/classify-from-image?${qs.stringify({
                    imageId,
                    draftId
                })}`
            );

        return response.data;
    }
);

export const fetchPredictionsFromImages = createAsyncThunk<
    void,
    void,
    { state: RootState }
>(
    'predictions/fetchPredictionsFromImages',
    async (_, { dispatch, getState }) => {
        const languageCodeIso2 = selectPreferredLanguageCode(getState());
        const existingShortDescription = selectShortDescription(getState());
        const existingLongDescription = selectLongDescription(getState());
        const isModalFeatureToggleEnabled = selectIsFeatureEnabled(
            'magical-syi-autofill-prompt'
        )(getState());
        const { itemClassification } =
            selectClassificationResponse(getState()) || {};

        const imageIds = selectImageIds(getState());
        const draftId = selectDraftId(getState());

        const response =
            await webApiClient.get<PredictionsFromDraftImageApiResponse>(
                `/selling/predict-from-images?${qs.stringify({
                    imageIds: imageIds,
                    languageCodeIso2,
                    itemClassification,
                    draftId: draftId
                })}`
            );

        trackSyiForm({
            eventName: 'magical_api_call',
            parameters: { draft_id: draftId }
        });

        // If we already have descriptions, we don't want to overwrite them with the predicted one without asking the user first.
        // If we don't have short and long description, we want to set the predicted ones
        if (response.data.shortDescription) {
            dispatch(
                setPredictedShortDescription(response.data.shortDescription)
            );

            if (existingShortDescription && isModalFeatureToggleEnabled) {
                dispatch(
                    setShouldSuggestShortDescription(
                        response.data.shortDescription !==
                            existingShortDescription
                    )
                );
            } else {
                dispatch(setHasUsedPredictedDescription(true));
                dispatch(setShortDescription(response.data.shortDescription));
            }
        }

        if (response.data.longDescription) {
            dispatch(
                setPredictedLongDescription(response.data.longDescription)
            );

            if (existingLongDescription && isModalFeatureToggleEnabled) {
                dispatch(
                    setShouldSuggestLongDescription(
                        response.data.longDescription !==
                            existingLongDescription
                    )
                );
            } else {
                dispatch(setHasUsedPredictedDescription(true));
                dispatch(setLongDescription(response.data.longDescription));
            }
        }

        dispatch(setNumProcessedImages(response.data.numProcessedImages));
        dispatch(setAiProcessingTime(response.data.aiProcessingTime));
    }
);

export const fetchConditionAndItemTypeFromCategory = createAsyncThunk(
    'syi/images/fetchConditionAndItemTypeFromCategory',
    async (
        { isCompany, categoryId }: ConditionListingTypeFromCategoryRequest,
        { dispatch }
    ) => {
        const response =
            await webApiClient.get<GetConditionAndListingTypeFromCategoryResponse>(
                `/selling/condition-listing-type-from-category?${qs.stringify({
                    isCompany,
                    categoryId
                })}`
            );

        if (response.data.condition) {
            dispatch(
                draftActions.setItemConditionFromPrediction(
                    response.data.condition
                )
            );
        }
        dispatch(
            priceActions.setIsPureBinSelectedFromPrediction(
                response.data.itemType === ItemTypes.PURE_BIN
            )
        );

        return response.data;
    }
);
